import { useMemo, useState } from "react";

import {
  ChevronDownIcon,
  FilterIcon,
  Button,
  MultiSelectDropdown,
  ToggleButton,
  ToggleButtonGroup,
  type MultiDropdownOption,
  type Selection,
} from "@stacklet/ui";

import { useSearchParamState } from "app/hooks";
import {
  cloudOrganizationPath,
  samlProviderEditPath,
  samlProviderPath,
} from "app/views/settings/access-control/IdentityProviders/constants";

import { GroupByDropdown, type GroupByOption } from "./GroupByDropdown";
import { providerLabel } from "./groupSections";
import {
  GroupSectionsGrid,
  type GroupSection,
  type GroupsView,
} from "./GroupSectionsGrid";
import { useIdentityProviderSources } from "./useIdentityProviderSources";

/** How the directory is grouped; "none" is one flat list of every group. */
type GroupBy = "identity-provider" | "none";

/** Group-by options, in display order. */
const GROUP_BY_OPTIONS: readonly GroupByOption<GroupBy>[] = [
  { id: "identity-provider", label: "Identity Provider" },
  { id: "none", label: "None" },
];

/** The querystring key the grouping persists under. */
const GROUP_BY_PARAM = "group-by";

/** The groupings a `?group-by=` value may name; anything else reads as the default. */
const GROUP_BY_IDS = GROUP_BY_OPTIONS.map((option) => option.id);

/**
 * Providers beyond which the identity-provider filter gains a search field
 * (the design system's threshold for a searchable dropdown).
 */
const SEARCHABLE_FILTER_THRESHOLD = 7;

/**
 * Applies a toggle selection. React Aria hands over a set; in single selection
 * mode it holds at most one key, and an empty set (the selected item clicked
 * again) keeps the current view rather than clearing it.
 */
function readView(keys: Selection): GroupsView | undefined {
  if (typeof keys === "string") return undefined;
  return [...keys][0] as GroupsView | undefined;
}

/**
 * The text a provider option is searched by: its explicit search label, else
 * its name when that's plain text. Matches how the dropdown itself names each
 * option for assistive tech.
 */
function optionSearchText(option: MultiDropdownOption): string {
  return (
    option.searchLabel ?? (typeof option.name === "string" ? option.name : "")
  );
}

interface IdpFilterProps {
  /** Every provider that can be filtered on. */
  options: MultiDropdownOption[];
  /** The currently selected providers; empty means "all". */
  value: MultiDropdownOption[];
  /** Called when the selection changes. */
  onChange: (selected: MultiDropdownOption[]) => void;
}

/**
 * The "Identity Provider" filter: a multiselect of the providers present,
 * narrowing which providers' groups are listed. An empty selection lists every
 * provider. Past a handful of providers the list gains a search field, and
 * Select All / Clear All actions sit above it.
 */
function IdpFilter({ options, value, onChange }: IdpFilterProps) {
  const [query, setQuery] = useState("");

  const summary =
    value.length === 0 ? "All" : value.map((option) => option.name).join(", ");

  const isSearchable = options.length > SEARCHABLE_FILTER_THRESHOLD;
  const visibleOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!isSearchable || !term) return options;
    return options.filter((option) =>
      `${optionSearchText(option)} ${option.assistiveText ?? ""}`
        .toLowerCase()
        .includes(term),
    );
  }, [options, query, isSearchable]);

  // Selects what the search currently shows, keeping anything already picked
  // (which the term may be hiding), rather than every provider regardless.
  const selectAll = () => {
    const selectedIds = new Set(value.map((option) => option.id));
    onChange([
      ...value,
      ...visibleOptions.filter((option) => !selectedIds.has(option.id)),
    ]);
  };

  return (
    <MultiSelectDropdown
      label="Identity Provider"
      onChange={onChange}
      onSearch={isSearchable ? setQuery : undefined}
      options={visibleOptions}
      placement="bottom right"
      searchPlaceholder="Search providers"
      searchValue={query}
      topCTA={{
        left: { children: "Select All", onClick: selectAll },
        right: { children: "Clear All", onClick: () => onChange([]) },
      }}
      trigger={
        <Button
          IconLeft={<FilterIcon size={14} />}
          IconRight={<ChevronDownIcon size={12} />}
          aria-label="Filter by identity provider"
          className="whitespace-nowrap py-xxs"
          variant="tertiary"
        >
          <span className="text-text-secondary">Identity Provider:</span>{" "}
          {summary}
        </Button>
      }
      value={value}
    />
  );
}

/**
 * The settings groups directory. Grouped by identity provider it's every
 * provider's groups in per-provider sections, plus a Local Groups section;
 * ungrouped it's one flat list of every group. The view toggle switches what an
 * expanded group shows (its role assignments or its members); the "Identity
 * Provider" filter narrows which providers' groups are listed. The grouping
 * lives in the URL, so the view survives a reload and can be linked to.
 *
 * Cloud orgs are a connection, so every page is loaded, not just the first.
 */
export function GroupsDirectory() {
  const { samlProviders, cloudOrganizations, loading } =
    useIdentityProviderSources();

  const [groupBy, setGroupBy] = useSearchParamState<GroupBy>(GROUP_BY_PARAM, {
    allowed: GROUP_BY_IDS,
    defaultValue: "identity-provider",
  });

  // The identity-provider sections: every SAML provider and cloud organization.
  const providerSections: GroupSection[] = useMemo(() => {
    const samlSections: GroupSection[] = samlProviders.map((provider) => ({
      id: provider.id,
      label: providerLabel(provider),
      secondary: provider.name,
      scope: { kind: "idp-namespace", idpNamespace: provider.idpNamespace },
      idpType: provider.idpType,
      to: samlProviderPath(provider.name),
      editTo: samlProviderEditPath(provider.name),
    }));

    // Label by the org's own name/alias: several orgs can share a provider
    // (e.g. multiple GCP orgs), so `provider` alone would show as duplicates.
    // The unique `key` stays as the secondary to tell them apart.
    const cloudSections: GroupSection[] = cloudOrganizations.map((node) => ({
      id: node.id,
      label: providerLabel(node),
      secondary: node.key,
      scope: { kind: "idp-namespace", idpNamespace: node.idpNamespace },
      idpType: node.idpType,
      to: cloudOrganizationPath(node.provider, node.key),
      editTo: null,
    }));

    return [...samlSections, ...cloudSections];
  }, [samlProviders, cloudOrganizations]);

  // Local groups (created in Stacklet, not synced from a provider) need their
  // own section; the exact idp-namespace scope would otherwise drop them entirely.
  const sections: GroupSection[] = useMemo(
    () => [
      ...providerSections,
      {
        id: "__local__",
        label: "Local Groups",
        secondary: null,
        scope: { kind: "local" },
        to: null,
        editTo: null,
      },
    ],
    [providerSections],
  );

  // Providers only, not the local section. `secondary` becomes assistive text so
  // providers sharing a label (e.g. same-named cloud orgs) stay distinguishable.
  const filterOptions: MultiDropdownOption[] = useMemo(
    () =>
      providerSections.map((section) => ({
        id: section.id,
        name: section.label,
        assistiveText: section.secondary ?? undefined,
      })),
    [providerSections],
  );

  const [view, setView] = useState<GroupsView>("role-assignments");
  const [selected, setSelected] = useState<MultiDropdownOption[]>([]);

  const handleViewChange = (keys: Selection) => {
    const next = readView(keys);
    if (next) setView(next);
  };

  const selectedIds = useMemo(
    () => new Set(selected.map((option) => option.id)),
    [selected],
  );
  const filteredSections = useMemo(
    () =>
      selectedIds.size === 0
        ? sections
        : sections.filter((section) => selectedIds.has(section.id)),
    [sections, selectedIds],
  );

  // Ungrouped: one headerless section over every group, narrowed to the
  // selected providers' namespaces when the filter is set (the same providers
  // the grouped view would keep).
  const flatSections: GroupSection[] = useMemo(
    () => [
      {
        id: "__all__",
        label: "All Groups",
        secondary: null,
        scope: {
          kind: "all",
          idpNamespaces:
            selectedIds.size === 0
              ? undefined
              : filteredSections.flatMap((section) =>
                  section.scope.kind === "idp-namespace"
                    ? [section.scope.idpNamespace]
                    : [],
                ),
        },
        to: null,
        editTo: null,
        hideHeader: true,
      },
    ],
    [filteredSections, selectedIds],
  );

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-center gap-sm">
        <span className="text-label-small text-text-secondary">View</span>
        <ToggleButtonGroup
          aria-label="View"
          onSelectionChange={handleViewChange}
          selectedKeys={new Set([view])}
          selectionMode="single"
        >
          <ToggleButton id="role-assignments">Role Assignments</ToggleButton>
          <ToggleButton id="users">Users</ToggleButton>
        </ToggleButtonGroup>
      </div>

      <GroupSectionsGrid
        loading={loading}
        sectionUnit="group"
        sections={groupBy === "none" ? flatSections : filteredSections}
        tableId="settings-groups"
        toolbarActions={
          <>
            <GroupByDropdown
              onChange={setGroupBy}
              options={GROUP_BY_OPTIONS}
              value={groupBy}
            />
            <IdpFilter
              onChange={setSelected}
              options={filterOptions}
              value={selected}
            />
          </>
        }
        view={view}
      />
    </div>
  );
}
